import { useEffect, useRef, useState } from "react";
import { Map, GoogleApiWrapper } from "google-maps-react";
import { EventsMarker } from "../EventsMarker";
import { useEventStore } from "stores/event/useEventStore";
import "./style.css";

/**
 * google-maps-react Map clones every child with map, google, and mapCenter.
 * Raw DOM elements would forward mapCenter to a div (React warning). This host
 * strips those props while keeping layout/className behavior.
 */
// eslint-disable-next-line no-unused-vars
function MapOverlay({ map, google, mapCenter, children, ...divProps }) {
  return <div {...divProps}>{children}</div>;
}

const MapWrapper = ({ google }) => {
  const mapRef = useRef(null);
  const eventInfoWindowRef = useRef(null);

  const selectedPlace = useEventStore((state) => state.selectedPlace);
  const zoom = useEventStore((state) => state.zoom);

  const [currentMarker, setCurrentMarker] = useState(null);

  useEffect(() => {
    if (selectedPlace && mapRef.current) {
      const google = window.google;

      if (currentMarker) {
        currentMarker.setMap(null);
      }

      if (selectedPlace) {
        const marker = new google.maps.Marker({
          position: selectedPlace.geometry?.location,
          map: mapRef.current,
          title: selectedPlace.name,
        });

        setCurrentMarker(marker);

        mapRef.current.setCenter(selectedPlace.geometry?.location);
        mapRef.current.setZoom(zoom); // Dynamically set the zoom level based on the `zoom` prop
      }
    }

    if (!selectedPlace && currentMarker) {
      currentMarker.setMap(null);
      setCurrentMarker(null);
    }
    // currentMarker is intentionally omitted: including it would tear down and
    // recreate the marker on every setCurrentMarker, looping the effect.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPlace, zoom]);

  const onMapReady = (mapProps, map) => {
    mapRef.current = map;
    eventInfoWindowRef.current = new mapProps.google.maps.InfoWindow({
      disableAutoPan: false,
    });
  };

  return (

    <Map
      google={google}
      onReady={onMapReady}
      zoomControl={false}
      streetViewControl={false}
      // initialCenter={{
      //   lat: 37.09024,
      //   lng: -95.712891,
      // }}
      zoom={3}
      mapTypeControl={false}
      fullscreenControl={false}
      containerStyle={{
        width: "100%",
        height: "623px",
        position: "relative",
        borderRadius: "10px",
        overflow: "hidden",
      }}
      style={{
        borderRadius: "10px",
        overflow: "hidden",
      }}
    >
      <EventsMarker
        map={mapRef.current}
        eventInfoWindowRef={eventInfoWindowRef}
      />
      {/* DealersMarker removed — SaddleFit authorized / fitting-room map pins */}

      {/* Legend — commented out for now
      <MapOverlay className="min-w-[300px] p-4 h-[100px] bg-[#FFF] rounded-[16px] lg:block hidden absolute bottom-4 right-4">
        <div>
          <h2 className="legend-text">Legend</h2>
          <div className="w-auto   h-[24px] mt-[16px]">
            <div className="flex justify-between align-middle select-none transition duration-500 ease-in">
              <div className="flex gap-x-[10px]">
                <LegendIcon />
                <p className="toggle-text"> {subdomain ? formatName(subdomain): "SaddleFit"} Events</p>
              </div>
              <label className="switch">
                <input type="checkbox" checked={eventToggle} onChange={handleEventsToggle} />
                <span className="slider-event"></span>
              </label>
            </div>
          </div>
        </div>
      </MapOverlay>

      <MapOverlay className={`w-[95%] p-4 ${showDetails ? "h-[100px]" : "h-[56px]"}  bg-[#FFF] rounded-[16px] lg:hidden block absolute top-4 left-[50%] transform translate-x-[-50%] z-10`}>
        <div>
          <div className="flex justify-between items-center">
            <h2 className="legend-text">Legend</h2>
            <span className="legend-text" onClick={handleToggleDetails}>{showDetails ? <MinusIcon /> : <AddIcon />}</span>
          </div>
          {showDetails && (
            <div className="w-full h-[24px] mt-[16px]">
              <div className="flex justify-between align-middle select-none transition duration-500 ease-in">
                <div className="flex gap-x-[10px]">
                  <LegendIcon />
                  <p className="toggle-text">Saddlefit Events</p>
                </div>
                <label className="switch">
                  <input type="checkbox" checked={eventToggle} onChange={handleEventsToggle} />
                  <span className="slider-event"></span>
                </label>
              </div>
            </div>
          )}
        </div>
      </MapOverlay>
      */}

      <MapOverlay className="gradient-container h-[150px] flex lg:static overflow-hidden rounded-[10px] justify-center lg:bg-none bg-slate-0 items-center absolute w-full" />
    </Map>
  );
};

export default GoogleApiWrapper({
  LoadingContainer: "div",
})(MapWrapper);
